import { useEffect, useRef, useState } from "react";
import Button from "../../../common/Button";
import { HiOutlinePlus } from "react-icons/hi";
import { MdClose } from "react-icons/md";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import Table from "../../../common/Table";
import Loader from "../../../common/Loader";
import { notify } from "../../../common/Toast";
import {
  MenuMasterAPI,
  MenuPermissionAPI,
  UsersAPI,
} from "../../../utlis/apiService";
import Pagination from "../../../common/Pagination";
import ConfirmDeleteModal from "../../../common/DeleteModel";
import ErrorModal from "../../../common/CustomModel";
import Model from "../../../common/Model";
import MenuPermissionForm from "./MenuPermissionForm";
import { RegistryHeader } from "../../../common/Shared";

const MenuPermission = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    menuId: [],
  });
  const [loading, setLoading] = useState(false);
  const [isMenusData, setIsMenusData] = useState([]);
  const [isUserData, setIsUserData] = useState([]);
  const [isMenuIDSData, setIsMenuIDSData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);

  const fetchMenusData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const res = await MenuPermissionAPI.getAllPagination(
        currentPage,
        pageSize,
      );
      const newItems = res.data;

      if (currentPage === 1) {
        setIsMenusData(newItems);
      } else {
        setIsMenusData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching location data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchMenusData();
    fetchUserData();
    fetchMenusIDSData();
  }, [currentPage]);

  const fetchUserData = async () => {
    try {
      const res = await UsersAPI.getAll();
      setIsUserData(res.map((d) => ({ value: d.user_Id, label: d.userName })));
    } catch (error) {
      notify("Error fetching location data", "error");
    }
  };

  const fetchMenusIDSData = async () => {
    try {
      const res = await MenuMasterAPI.getAll();
      setIsMenuIDSData(
        res.map((d) => ({ value: d.menu_ID, label: d.menu_Name })),
      );
    } catch (error) {
      notify("Error fetching location data", "error");
    }
  };

  const handleAdd = () => {
    setIsModelOpen(true);
  };

  const handleEdit = async (userId) => {
    try {
      const data = await MenuPermissionAPI.getById(userId);
      if (data && data.length > 0) {
        setFormData({
          userId: data[0].userId,
          permissions: data.map((item) => ({
            menuId: item.menuId,
            insert: item.isInsert,
            update: item.isUpdate,
            delete: item.isDelete,
            view: item.isView,
          })),
        });
      }
      setIsEditing(true);
      setIsModelOpen(true);
    } catch (error) {
      notify("Failed to fetch menu permission", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await MenuPermissionAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchMenusData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSave = async (payload) => {
    try {
      await MenuPermissionAPI.saveOrUpdate(payload);
      setIsModelOpen(false);
      fetchMenusData();
      notify("Saved successfully", "success");
    } catch {
      notify("Failed to save", "error");
    }
  };

  return (
    <>
      <div className="flex flex-col h-full bg-white font-plus overflow-hidden">
        <RegistryHeader title="Menu Permission">
          <div className="relative w-full sm:w-80 group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
              <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search by alloy name..."
              className="block w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
              >
                <MdClose size={18} />
              </button>
            )}
          </div>

          <button
            onClick={handleAdd}
            className="bg-[#3F80F7] text-white font-black text-[10px] uppercase tracking-[3px] px-8 py-3 border border-[#3F80F7] hover:bg-[#2c5ebc] transition-all flex items-center gap-3"

          >
            <HiOutlinePlus size={16} /> New Menu Permissions
          </button>
        </RegistryHeader>

        <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
          <div
            ref={tableScrollRef}
            className="relative overflow-auto border border-gray-200 bg-white flex-1"
          >
            <div className="min-w-max">
              {loading && <Loader centered />}
              <Table
                columns={[
                  { key: "user_Name", label: "User Name" },
                  { key: "menuNames", label: "Menu Name" },
                ]}
                data={isMenusData}
                onEdit={(row) => handleEdit(row.userId)}
                onDelete={(row) => handleDelete(row.userId)}
              />
            </div>
          </div>
        </div>
        <Model
          isOpen={isModelOpen}
          onClose={() => setIsModelOpen(false)}
          name={"Add Menu Permission"}
        >
          <MenuPermissionForm
            isUserData={isUserData}
            isMenuIDSData={isMenuIDSData}
            onCancel={() => setIsModelOpen(false)}
            onSubmit={handleSave}
            isEditing={isEditing}
            formData={formData}
          />
        </Model>

        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
        />
        {errorModalOpen && (
          <ErrorModal
            message={errorMessage}
            subMessage={errorSubMessage}
            onClose={() => setErrorModalOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default MenuPermission;
